import { BaseTariffPage } from "@/components/tarif/BaseTariffPage";
import { Shield, AlertCircle, CheckCircle2, Clock, Phone, FileText, Stethoscope, Users, Building2, Hospital, Lock, Globe, Zap, HeartPulse } from "lucide-react";

const STOIK_COLOR = "#1362dd";

const StoikTarif = () => {
  const heroFeatures = [
    { icon: Phone, title: "24/7 Cyber-Hotline", subtitle: "Schnelle Hilfe" },
    { icon: CheckCircle2, title: "Digitaler Schutz", subtitle: "Modern & umfassend" },
    { icon: Shield, title: "Bis 5 Mio. €", subtitle: "Deckungssumme" }
  ];

  const coverageAreas = [
    {
      icon: Lock,
      title: "Cyber-Eigenschäden",
      items: [
        "Datenrettung nach Cyber-Angriffen",
        "IT-Forensik und Schadensanalyse",
        "Wiederherstellung von Systemen",
        "Betriebsunterbrechung durch Cyber-Vorfälle",
        "Kosten für externe IT-Spezialisten"
      ]
    },
    {
      icon: Globe,
      title: "Haftpflichtschäden",
      items: [
        "Datenschutzverletzungen (DSGVO)",
        "Verlust von Patientendaten",
        "Schadensersatzansprüche Dritter",
        "Rechtsverteidigungskosten",
        "Bußgelder und Strafen"
      ]
    },
    {
      icon: AlertCircle,
      title: "Cyber-Erpressung",
      items: [
        "Lösegeldzahlungen bei Ransomware",
        "Verhandlungskosten mit Erpressern",
        "Krisenmanagement und PR",
        "Wiederherstellung verschlüsselter Daten",
        "Präventive Sicherheitsberatung"
      ]
    },
    {
      icon: FileText,
      title: "Rechtsbeistand & Assistance",
      items: [
        "Anwaltskosten bei Cyber-Vorfällen",
        "Meldung an Datenschutzbehörden",
        "Benachrichtigung betroffener Patienten",
        "24/7 Cyber-Notfall-Hotline",
        "IT-Sicherheitsberatung"
      ]
    }
  ];

  const targetGroups = [
    {
      icon: Stethoscope,
      title: "Hausärzte",
      description: "Allgemeinmediziner mit hoher Patientendatendichte",
      benefits: [
        "Schutz sensibler Patientenakten",
        "Absicherung bei Praxisausfall",
        "24/7 IT-Notfall-Hotline",
        "DSGVO-konforme Schadenabwicklung"
      ],
      recommendation: "Empfohlene Deckung: 2-3 Mio. €"
    },
    {
      icon: HeartPulse,
      title: "Fachärzte",
      description: "Spezialisten mit hochsensiblen Gesundheitsdaten",
      benefits: [
        "Schutz vor Cyber-Erpressung",
        "IT-Forensik nach Angriffen",
        "Kostenübernahme bei Datenverlust",
        "Rechtsbeistand bei DSGVO-Verstößen"
      ],
      recommendation: "Empfohlene Deckung: 3-5 Mio. €"
    },
    {
      icon: Users,
      title: "Gemeinschaftspraxen",
      description: "Mehrere Ärzte mit gemeinsamer IT-Infrastruktur",
      benefits: [
        "Umfassender Schutz aller Partner",
        "Cyber-Krisenmanagement",
        "Betriebsunterbrechungsschutz",
        "Externe IT-Sicherheitsberatung"
      ],
      recommendation: "Empfohlene Deckung: 3-5 Mio. €"
    },
    {
      icon: Building2,
      title: "MVZ & Kliniken",
      description: "Große medizinische Einrichtungen mit komplexer IT",
      benefits: [
        "Höchste Deckungssummen verfügbar",
        "Spezialisiertes Krisenmanagement",
        "Schutz vor Massenschadensersatz",
        "Individuelle Risikobewertung"
      ],
      recommendation: "Empfohlene Deckung: 5+ Mio. €"
    }
  ];

  const insuranceConditions = [
    {
      icon: Shield,
      title: "Digitale Schadenabwicklung",
      description: "Moderne, volldigitale Prozesse für schnelle Regulierung"
    },
    {
      icon: Clock,
      title: "48h Reaktionszeit",
      description: "Garantierte Erstreaktion binnen 48 Stunden"
    },
    {
      icon: CheckCircle2,
      title: "Keine Wartezeiten",
      description: "Sofortschutz ab Versicherungsbeginn"
    },
    {
      icon: Phone,
      title: "24/7 Cyber-Notfall-Hotline",
      description: "Rund um die Uhr erreichbare Experten"
    },
    {
      icon: Globe,
      title: "Weltweiter Schutz",
      description: "Versicherungsschutz auch im Ausland"
    },
    {
      icon: Zap,
      title: "Präventive Services",
      description: "IT-Sicherheitsberatung und Präventionsmaßnahmen inklusive"
    }
  ];

  const faqs = [
    {
      question: "Was macht die Stoik Cyberversicherung besonders?",
      answer: "Stoik ist ein digitaler Versicherer mit vollständig digitalisierter Schadenabwicklung. Die Kombination aus moderner Technologie und schnellen Prozessen macht Stoik besonders attraktiv für digital affine Praxen."
    },
    {
      question: "Wie schnell erfolgt die Schadensregulierung bei Stoik?",
      answer: "Stoik garantiert eine Erstreaktion innerhalb von 48 Stunden. Durch digitale Prozesse erfolgt die Schadenabwicklung deutlich schneller als bei traditionellen Versicherern."
    },
    {
      question: "Sind Lösegeldzahlungen bei Ransomware versichert?",
      answer: "Ja, die Stoik Cyberversicherung deckt Lösegeldzahlungen im Rahmen der vereinbarten Deckungssumme ab. Zusätzlich werden Verhandlungskosten und Wiederherstellungsmaßnahmen übernommen."
    },
    {
      question: "Welche Deckungssummen bietet Stoik für Arztpraxen?",
      answer: "Stoik bietet flexible Deckungssummen von 1 bis 5 Millionen Euro. Für Hausarztpraxen empfehlen wir 2-3 Mio. €, für Fachärzte und MVZ 3-5 Mio. €."
    },
    {
      question: "Gibt es eine Selbstbeteiligung bei Stoik?",
      answer: "Ja, in der Regel gibt es eine Selbstbeteiligung. Die genaue Höhe hängt vom gewählten Tarif ab und kann individuell vereinbart werden."
    },
    {
      question: "Sind auch Social Engineering Schäden versichert?",
      answer: "Ja, Stoik versichert auch Schäden durch Social Engineering wie CEO-Fraud oder gefälschte Zahlungsaufforderungen im Rahmen der vereinbarten Bedingungen."
    },
    {
      question: "Wie unterscheidet sich Stoik von traditionellen Versicherern?",
      answer: "Stoik setzt auf vollständig digitale Prozesse, schnellere Reaktionszeiten und moderne Kommunikationswege. Die Schadenabwicklung erfolgt komplett online, was Zeit und Aufwand spart."
    },
    {
      question: "Gibt es Präventionsleistungen bei Stoik?",
      answer: "Ja, Stoik bietet IT-Sicherheitsberatung und präventive Services an, um Cyber-Risiken von vornherein zu minimieren."
    },
    {
      question: "Was passiert bei einem Datenschutzverstoß nach DSGVO?",
      answer: "Stoik übernimmt Bußgelder, Anwaltskosten, Meldepflichten an Behörden und Benachrichtigungskosten betroffener Patienten."
    },
    {
      question: "Wie läuft der Abschluss bei Stoik ab?",
      answer: "Der Abschluss erfolgt komplett digital. Nach einer Risikoanalyse erhalten Sie ein individuelles Angebot, das Sie online annehmen können. Der Versicherungsschutz beginnt sofort."
    }
  ];

  const documents = [
    {
      title: "Stoik Cyberversicherung - Produktinformationsblatt",
      type: "PDF",
      url: "/documents/stoik-cyberversicherung-pib.pdf"
    },
    {
      title: "Stoik Versicherungsbedingungen Cyber",
      type: "PDF",
      url: "/documents/stoik-cyber-avb.pdf"
    }
  ];

  return (
    <BaseTariffPage
      companyName="Stoik"
      companyColor={STOIK_COLOR}
      productRating={{ value: 4.5, count: 178 }}
      priceRange="65-230"
      pageTitle="Stoik Cyberversicherung für Ärzte & Praxen 2025"
      metaDescription="Stoik Cyberversicherung für Arztpraxen, MVZ & Kliniken ✓ Digitale Schadenabwicklung ✓ 24/7 Cyber-Hotline ✓ Bis 5 Mio. € Deckung ✓ Ransomware-Schutz ✓ DSGVO-Absicherung. Jetzt beraten lassen!"
      metaKeywords="Stoik Cyberversicherung, Stoik Cyber Arzt, Stoik Praxis, digitale Cyberversicherung, Stoik Versicherung Medizin, Cyber Arztpraxis Stoik, Ransomware Stoik"
      canonicalUrl="https://praxis-versichert.de/tarifwissen/stoik"
      heroLabel="Digitaler Versicherer mit modernem Cyber-Schutz"
      heroTitle="Stoik Cyberversicherung für medizinische Praxen"
      heroDescription="Schützen Sie Ihre Praxis mit der digitalen Cyberversicherung von Stoik. Schnelle Schadenabwicklung, 24/7 Support und umfassender Schutz vor Cyber-Risiken – speziell für Ärzte und medizinische Einrichtungen."
      heroFeatures={heroFeatures}
      alertTitle="Warum Stoik?"
      alertDescription="Stoik kombiniert moderne Versicherungstechnologie mit schnellen, digitalen Prozessen. Als innovativer Versicherer bietet Stoik speziell auf die Bedürfnisse von Arztpraxen zugeschnittene Cyber-Absicherung mit besonders kurzen Reaktionszeiten."
      coverageAreas={coverageAreas}
      targetGroups={targetGroups}
      insuranceConditions={insuranceConditions}
      faqs={faqs}
      documents={documents}
    />
  );
};

export default StoikTarif;
